import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  AcademicCapIcon, 
  UserGroupIcon, 
  ClockIcon, 
  ShieldCheckIcon,
  MapPinIcon,
  StarIcon,
  CurrencyDollarIcon,
  SparklesIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const AboutPage = () => {
  const [stats, setStats] = useState({
    announcementCount: 0,
    categoryCount: 0,
    teacherCount: 0,
    studentCount: 0
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await fetch('/v1/dashboard/counts');
        const data = await response.json();
        if (data.success && data.data) {
          setStats(data.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchDashboardData();
  }, []);

  const features = [
    {
      icon: <AcademicCapIcon className="h-10 w-10" />,
      title: "Uzman Eğitmenler",
      description: "Alanında deneyimli ve uzman eğitmenlerle birebir özel ders imkanı."
    },
    {
      icon: <ClockIcon className="h-8 w-8" />,
      title: "Esnek Zamanlama",
      description: "Size uygun gün ve saatlerde ders alma imkanı ile eğitimde esneklik."
    },
    {
      icon: <MapPinIcon className="h-8 w-8" />,
      title: "Çoklu Ders Seçeneği",
      description: "Online, öğrencinin evinde veya eğitmenin ofisinde ders alma seçenekleri."
    },
    {
      icon: <ShieldCheckIcon className="h-8 w-8" />,
      title: "Güvenli Platform",
      description: "Güvenilir ödeme sistemi ve onaylı eğitmen profilleriyle güvenli eğitim."
    }
  ];

  const benefits = [
    {
      icon: <StarIcon className="h-6 w-6" />,
      title: "Öğrenciler İçin",
      items: [
        "İhtiyacınıza uygun eğitmenleri kolayca bulun",
        "Eğitmen profillerini ve değerlendirmelerini inceleyin",
        "Ders taleplerinizi hızlıca iletin",
        "Esnek ders programları oluşturun",
        "Online veya yüz yüze ders seçeneği"
      ]
    },
    {
      icon: <CurrencyDollarIcon className="h-6 w-6" />,
      title: "Eğitmenler İçin",
      items: [
        "Boş zamanlarınızı değerlendirin",
        "Kendi ders ücretlerinizi belirleyin",
        "İlan yayınlayarak öğrencilere ulaşın",
        "Esnek çalışma imkanı",
        "Güvenli ödeme sistemi"
      ]
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 overflow-hidden">
      <Helmet>
        <title>Hakkımızda | DersMaster - Özel Ders ve Eğitim Platformu</title>
        <meta name="description" content="DersMaster, öğrenciler ve eğitmenler için yenilikçi, güvenli ve esnek bir özel ders platformudur. Hakkımızda daha fazla bilgi edinin." />
        <meta name="keywords" content="hakkımızda, dersmaster, özel ders, eğitim platformu, eğitmen, öğrenci, online ders, güvenli eğitim, birebir ders" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="DersMaster" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Hakkımızda | DersMaster - Özel Ders ve Eğitim Platformu" />
        <meta property="og:description" content="DersMaster, öğrenciler ve eğitmenler için yenilikçi, güvenli ve esnek bir özel ders platformudur. Hakkımızda daha fazla bilgi edinin." />
        <meta property="og:url" content="https://dersmaster.com/about" />
        <meta property="og:image" content="https://dersmaster.com/logo192.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Hakkımızda | DersMaster - Özel Ders ve Eğitim Platformu" />
        <meta name="twitter:description" content="DersMaster, öğrenciler ve eğitmenler için yenilikçi, güvenli ve esnek bir özel ders platformudur. Hakkımızda daha fazla bilgi edinin." />
        <meta name="twitter:image" content="https://dersmaster.com/logo192.png" />
        <link rel="canonical" href="https://dersmaster.com/about" />
        <html lang="tr" />
      </Helmet>
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Patterns */}
        <div className="absolute inset-0 z-0 opacity-40 dark:opacity-20">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-blue-300 dark:bg-blue-700 filter blur-3xl"></div>
          <div className="absolute top-1/2 -left-24 w-80 h-80 rounded-full bg-indigo-300 dark:bg-indigo-800 filter blur-3xl"></div>
          <div className="absolute -bottom-24 right-1/4 w-64 h-64 rounded-full bg-purple-300 dark:bg-purple-800 filter blur-3xl"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white leading-tight">
                  Öğrenmenin <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">Yeni Nesil</span> Platformu
                </h1>
                <p className="mt-6 text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-3xl">
                  Dersmaster, eğitmenleri ve öğrencileri bir araya getiren, özel ders deneyimini 
                  daha erişilebilir ve esnek hale getiren yenilikçi bir platformdur.
                </p>
              </motion.div>
            </div>
            
            {/* Stats Cards - Animated Grid */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="mt-12 lg:mt-0 grid grid-cols-2 gap-4"
            >
              {[
                { title: "Aktif Eğitmen", value: stats.teacherCount, color: "from-blue-500 to-blue-600" },
                { title: "Ziyaretçi", value: 10250, color: "from-indigo-500 to-indigo-600" },
                { title: "Kategori", value: stats.categoryCount, color: "from-purple-500 to-purple-600" },
                { title: "Aktif İlan", value: stats.announcementCount, color: "from-pink-500 to-pink-600" }
              ].map((stat, i) => (
                <motion.div
                  key={stat.title}
                  variants={itemVariants}
                  className={`bg-gradient-to-br ${stat.color} rounded-2xl p-6 text-white shadow-lg transform transition-transform hover:scale-105`}
                >
                  <div className="text-3xl sm:text-4xl font-bold">{stat.value}+</div>
                  <div className="text-sm sm:text-base font-medium text-white/80">{stat.title}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Features Section - Staggered Cards */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-base font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">Benzersiz Özellikler</h2>
            <p className="mt-2 text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
              Neden Dersmaster?
            </p>
            <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-500 dark:text-gray-300">
              Eğitim hayatınızı kolaylaştıran yenilikçi çözümler sunuyoruz
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                variants={itemVariants}
                className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all border border-gray-100 dark:border-gray-700 relative overflow-hidden group"
              >
                {/* Feature background accent */}
                <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-blue-100 dark:bg-blue-900/30 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-all duration-300"></div>
                
                <div className="relative z-10">
                  <div className="text-blue-600 dark:text-blue-400 mb-5 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg inline-block">
                    {feature.icon}
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    {feature.title}
                  </h3>
                  
                  <p className="text-gray-600 dark:text-gray-300">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section - Alternating Layout */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-base font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">Avantajlar</h2>
            <p className="mt-2 text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
              Herkese Uygun Çözümler
            </p>
          </motion.div>
          
          <div className="space-y-20">
            {benefits.map((benefit, index) => (
              <motion.div 
                key={benefit.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className={`md:grid md:grid-cols-2 md:gap-12 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
              >
                <div className={`mb-10 md:mb-0 ${index % 2 === 1 ? 'md:order-2' : ''}`}>
                  <div className={`px-6 py-8 bg-gradient-to-br ${index === 0 ? 'from-blue-600 to-indigo-700' : 'from-indigo-600 to-purple-700'} rounded-2xl shadow-xl text-white`}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-3 bg-white/10 rounded-lg">
                        {benefit.icon}
                      </div>
                      <h3 className="text-2xl font-bold">
                        {benefit.title}
                      </h3>
                    </div>
                    <ul className="space-y-4">
                      {benefit.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center mt-0.5">
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <span className="text-lg">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    {index === 0 ? 'Öğrenciler İçin Mükemmel Deneyim' : 'Eğitmenler İçin Sınırsız Fırsatlar'}
                  </h3>
                  <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                    {index === 0 
                      ? 'İhtiyaçlarınıza uygun eğitmenler, esnek ders saatleri ve kişiselleştirilmiş öğrenme deneyimi ile akademik hedeflerinize ulaşın.'
                      : 'Esnek çalışma saatleri, kendi belirlediğiniz ücretler ve geniş öğrenci ağı ile mesleki deneyiminizi değerli kılın.'}
                  </p>
                  <div className="p-4 bg-white dark:bg-gray-700 rounded-lg shadow border border-gray-100 dark:border-gray-600">
                    <div className="flex items-center">
                      <SparklesIcon className="w-10 h-10 text-yellow-500 mr-4" />
                      <div>
                        <h4 className="font-bold text-gray-900 dark:text-white">
                          {index === 0 ? 'Hızlı İlerleyin' : 'Kazancınızı Artırın'}
                        </h4>
                        <p className="text-gray-600 dark:text-gray-300">
                          {index === 0 
                            ? 'Birebir öğrenme ile grup derslerinden 2-3 kat daha hızlı gelişme.'
                            : 'Boş saatlerinizi değerlendirerek ek gelir elde etme fırsatı.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl px-8 py-16 shadow-xl relative overflow-hidden"
          >
            {/* Background decorations */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
              <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-white/10"></div>
              <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-white/10"></div>
            </div>
            
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
                Öğrenme Yolculuğunuza Bugün Başlayın
              </h2>
              <p className="text-xl text-blue-100 mb-8 max-w-xl mx-auto">
                İster bilgi edinmek, ister becerilerinizi geliştirmek isteyin, 
                Dersmaster'da sizi bekleyen fırsatları keşfedin.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="/register" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-700 font-medium rounded-xl shadow-lg hover:bg-blue-50 transition-colors"
                >
                  Öğrenci Olarak Başla
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </a>
                <a 
                  href="/register" 
                  className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-medium rounded-xl hover:bg-white/10 transition-colors"
                >
                  Eğitmen Olarak Başla
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
